/** @sfdc-extension-file SNK */
'use client';

import { useMemo, type ComponentProps } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { Region, type ComponentType } from '@/components/region';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
import { Faq as FaqFeature } from '@/features/faq';

export const FAQ_DEFAULT_BACKGROUND_COLOR = '#F6F2ED';
const HEX_COLOR_REGEX = /^#(?:[0-9A-Fa-f]{3}){1,2}$/;

interface FaqProps extends ComponentProps<'section'> {
    title?: string;
    backgroundColor?: string;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - decorator functionality is tested separately */
@Component('faq', {
    name: 'FAQ',
    description: 'A titled FAQ accordion for SEO and supporting content on product listing pages.',
    group: 'SFN_Content',
})
@RegionDefinition([
    {
        id: 'sections',
        name: 'FAQ sections',
        description: 'Add and order as many FAQ Section components as needed.',
        componentTypeInclusions: ['faqSection'],
    },
])
// oxlint-disable-next-line react/only-export-components -- exported metadata is used to enrich nested regions in design mode
export class FaqMetadata {
    @AttributeDefinition({
        id: 'title',
        name: 'Title',
        description: 'Heading displayed above the FAQ accordion.',
        type: 'string',
        required: true,
        defaultValue: 'FAQs',
    })
    title?: string;

    @AttributeDefinition({
        id: 'backgroundColor',
        name: 'Background colour',
        description: 'Hex colour applied to the complete FAQ component.',
        type: 'string',
        defaultValue: FAQ_DEFAULT_BACKGROUND_COLOR,
    })
    backgroundColor?: string;
}
/* v8 ignore stop */

// oxlint-disable-next-line react/only-export-components -- Page Designer registry expects `fallback` from the component entry
export function fallback() {
    return <div className="min-h-48 w-full animate-pulse bg-muted" aria-hidden="true" />;
}

function safeBackgroundColor(value: string | undefined): string {
    return value && HEX_COLOR_REGEX.test(value) ? value : FAQ_DEFAULT_BACKGROUND_COLOR;
}

export default function Faq({
    className,
    style,
    title = 'FAQs',
    backgroundColor = FAQ_DEFAULT_BACKGROUND_COLOR,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...props
}: FaqProps) {
    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, FaqMetadata) : undefined),
        [component]
    );
    return (
        <FaqFeature.Root
            title={title}
            className={className}
            {...props}
            style={{ ...style, backgroundColor: safeBackgroundColor(backgroundColor) }}>
            {componentWithRegionMetadata ? (
                <FaqFeature.Accordion>
                    <Region component={componentWithRegionMetadata} regionId="sections" errorElement={null} />
                </FaqFeature.Accordion>
            ) : null}
        </FaqFeature.Root>
    );
}
